import { useInfiniteQuery, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "hooks/useRedux";
import { selectCurrency } from "redux/slices/currency";
import likeService from "services/like";
import { useRouter } from "next/router";
import { useAuth } from "contexts/auth/auth.context";
import LikedContainer from "containers/liked/liked";
import { Product } from "interfaces";
import productService from "services/product";
import SEO from "components/seo";
import useDebounce from "hooks/useDebounce";
import { useMemo } from "react";
import SettingsContainer from "containers/settings/settings";

export default function LikedProductsPage() {
  const { i18n } = useTranslation();
  const { query } = useRouter();
  const { isAuthenticated } = useAuth();
  const currency = useAppSelector(selectCurrency);
  const { productList } = useAppSelector((state) => state.liked);

  const lang = i18n.language;
  const debouncedProductList = useDebounce(productList, 500);

  const {
    data: serverLikedProducts,
    isLoading: isServerLikedProductsLoading,
    isFetching: isServerLikedProductsFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["likedProducts", lang, currency?.id, query?.shop_id],
    queryFn: ({ pageParam = 1 }) =>
      likeService.getAll({
        type: "product",
        perPage: 12,
        page: pageParam,
        currency_id: currency?.id,
        shop_id: query?.shop_id || undefined,
      }),
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
    },
    enabled: isAuthenticated,
    staleTime: 0,
  });

  const { data: localLikedProducts, isLoading: isLocalLikedProductsLoading } =
    useQuery({
      queryKey: [
        "likedUnAuthProducts",
        lang,
        currency?.id,
        debouncedProductList,
      ],
      queryFn: () => {
        const params = {
          currency_id: currency?.id,
          shop_id: query?.shop_id || undefined,
          perPage: 100,
          ...debouncedProductList.reduce(
            (acc: any, curr: any, index: number) => ({
              ...acc,
              [`products[${index}]`]: curr.productId,
            }),
            {},
          ),
        };
        return productService.getByIds(params);
      },
      enabled: !isAuthenticated && !!debouncedProductList.length,
      keepPreviousData: true,
      staleTime: 0,
    });

  const extractedServerLikedProducts = serverLikedProducts?.pages?.flatMap(
    (page) => page?.data || [],
  );

  const actualList: Product[] = useMemo(() => {
    if (isAuthenticated) {
      return extractedServerLikedProducts || [];
    }
    if (productList.length) {
      return localLikedProducts?.data || [];
    }
    return [];
  }, [
    isAuthenticated,
    extractedServerLikedProducts,
    localLikedProducts,
    productList,
  ]);

  return (
    <>
      <SEO />
      <SettingsContainer>
        <LikedContainer
          type="product"
          data={actualList}
          isLoading={
            isAuthenticated
              ? isServerLikedProductsLoading && isServerLikedProductsFetching
              : isLocalLikedProductsLoading
          }
          hasNextPage={hasNextPage}
          isFetchingNextPage={isServerLikedProductsFetching}
          fetchingNextPage={fetchNextPage}
        />
      </SettingsContainer>
    </>
  );
}
